<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else>
    <MainHeader :title="'Accounts'">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'UserAccount' })"
        />
      </template>
    </MainHeader>

    <div class="view ca-view content-padding">
      <!-- Action buttons -->
      <div class="flex-med-buttons mb-2">
        <button
          v-if="!isUserOver18"
          :disabled="!premiumUser"
          class="teal-button w-100 mb-1 mb-med-none"
          @click="accountConnectionSelection"
          data-pt-name="BankAccountLinked"
          data-pt-property-provider="Plaid"
        >
          <font-awesome-icon
            :icon="['fas', 'building-columns']"
            style="margin-right: 6px"
          />
          Connect Bank
        </button>
        <button
          class="teal-outline-button w-100 mb-1 mb-med-none"
          @click="router.push({ name: 'AddAccount' })"
        >
          + Create Account
        </button>
      </div>

      <!-- Info banner -->
      <div v-if="infoMessage" class="info-banner mb-2">
        <div class="info-banner-inner">
          <font-awesome-icon :icon="['fas', 'circle-info']" class="info-icon" />
          <p class="info-text">{{ infoMessage }}</p>
        </div>
        <button
          v-if="pendingUpdateItemId"
          class="gradient-button w-100 mt-1"
          @click="startUpdateFlow"
        >
          Refresh bank connection
        </button>
      </div>

      <!-- Account list -->
      <div v-if="accounts && accounts.length > 0">
        <div
          v-for="(institutionAccounts, institutionName) in groupedAccounts"
          :key="institutionName"
          class="bank-group mb-2"
        >
          <span class="section-label">{{ institutionName }}</span>

          <div class="bank-accounts-card">
            <div
              v-for="account in institutionAccounts"
              :key="account"
              class="account-row"
              @click="editAccount(account)"
            >
              <div class="account-icon-wrap">
                <font-awesome-icon
                  :icon="
                    account.icon
                      ? parseIconString(account.icon)
                      : ['fas', 'building-columns']
                  "
                  :style="{ color: account.iconColor || '#7F77DD' }"
                  style="font-size: 14px"
                />
              </div>
              <div class="account-info">
                <span class="account-name">{{ account.displayName }}</span>
                <span class="account-type">{{ account.type }}</span>
              </div>
              <font-awesome-icon
                :icon="['fas', 'chevron-right']"
                class="account-chevron"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <EmptyState
        v-else
        title="No accounts yet"
        subtitle="Connect your bank to sync automatically, or create a manual account to start tracking."
      >
        <template #icon>
          <svg viewBox="0 0 72 72" fill="none">
            <rect x="4" y="4" width="64" height="64" rx="16" fill="#E1F5EE" />
            <rect
              x="16"
              y="28"
              width="40"
              height="26"
              rx="5"
              stroke="#5DCAA5"
              stroke-width="2"
            />
            <path d="M16 36h40" stroke="#5DCAA5" stroke-width="2" />
            <circle cx="26" cy="46" r="3" fill="#5DCAA5" opacity="0.6" />
            <rect
              x="32"
              y="43"
              width="16"
              height="5"
              rx="2.5"
              fill="#5DCAA5"
              opacity="0.4"
            />
          </svg>
        </template>
        <template #actions>
          <button
            v-if="!isUserOver18"
            :disabled="!premiumUser"
            class="gradient-button w-100"
            @click="accountConnectionSelection"
          >
            Connect a bank account
          </button>
          <button
            class="outline-button w-100"
            @click="router.push({ name: 'AddAccount' })"
          >
            Create a manual account
          </button>
        </template>
      </EmptyState>
    </div>
  </div>
</template>

<script>
import { useRouter } from 'vue-router';
import { useMyStore } from '@/store.js';
import { onBeforeMount, onMounted, ref, watchEffect, computed } from 'vue';
import { createPlaidHandler } from '../../utils/plaidHelper';
import MainHeader from '../../components/View-Header.vue';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
import EmptyState from '../../components/EmptyState.vue';

export default {
  components: { MainHeader, SkeletonLoader, EmptyState },
  setup() {
    const router = useRouter();
    const store = useMyStore();
    const linkToken = ref('');
    let accounts = ref([]);
    const loading = ref(true);
    const premiumUser = computed(() => store.user.premiumSubscription);
    const infoMessage = ref('');
    const pendingUpdateInstitutionId = ref(null);
    const pendingUpdateItemId = ref(null);

    const groupedAccounts = computed(() => {
      const result = {};
      accounts.value.forEach((account) => {
        if (!result[account.institutionName]) {
          result[account.institutionName] = [];
        }
        result[account.institutionName].push(account);
      });
      const sortedResult = {};
      Object.keys(result)
        .sort()
        .forEach((institutionName) => {
          sortedResult[institutionName] = result[institutionName].sort((a, b) =>
            a.displayName.localeCompare(b.displayName)
          );
        });
      return sortedResult;
    });

    const accountConnectionSelection = () => {
      linkAccountAfterSelection();
    };

    const linkAccountAfterSelection = () => {
      try {
        store.createLinkToken().then(() => {
          linkToken.value = store.linkToken;
          localStorage.setItem('plaid_link_token', linkToken.value);
          openPlaidLink(linkToken.value);
        });
      } catch (error) {
        console.log('Error creating link token:', error);
      }
    };

    const openPlaidLink = (token) => {
      const handler = createPlaidHandler({ token, onSuccess, onEvent, onExit });
      handler.open();
    };

    window.addEventListener('PlaidOAuthRedirect', () => {
      try {
        const token =
          linkToken.value || localStorage.getItem('plaid_link_token');
        if (!token) return;
        const handler = createPlaidHandler({ token, onSuccess, onExit });
        handler.open();
      } catch (err) {
        console.error('Resume after OAuth failed:', err);
      }
    });

    const onSuccess = async (publicToken, metadata) => {
      try {
        loading.value = true;
        const resp = await store.exchangePublicToken(
          publicToken,
          metadata.institution,
          metadata.accounts
        );

        if (resp?.data?.requiresReauthentication) {
          infoMessage.value =
            `Looks like ${metadata.institution?.name || 'this bank'} is already connected. ` +
            `To keep your data in sync, please refresh the connection below.`;
          pendingUpdateItemId.value = resp.data.itemId;
          return;
        }

        // if (resp?.data?.requiresReauthentication) {
        //   infoMessage.value =
        //     `Looks like ${metadata.institution?.name || 'this bank'} is already connected. ` +
        //     `To keep your data in sync, please refresh the connection below.`;
        //   pendingUpdateInstitutionId.value =
        //     metadata.institution.institution_id;
        //   return;
        // }

        if (resp?.data?.success) {
          await store.getPlaidAccountsList();
          await store.syncPlaidTransactions();
        }
      } catch (error) {
        console.error('Error handling Plaid success:', error);
        if (
          error.message.includes('duplicate') ||
          error.response?.data?.requiresReauthentication
        ) {
          infoMessage.value = `This bank is already linked. Please refresh the connection below so we can keep your data up to date.`;
          pendingUpdateItemId.value = error.response?.data?.itemId;
          return;
        }
        // if (
        //   error.message.includes('duplicate') ||
        //   error.response?.data?.requiresReauthentication
        // ) {
        //   infoMessage.value = `This bank is already linked. Please refresh the connection below so we can keep your data up to date.`;
        //   pendingUpdateInstitutionId.value =
        //     metadata.institution.institution_id;
        //   return;
        // }
      } finally {
        loading.value = false;
        removePlaidLinkToken();
      }
    };

    const onEvent = (eventName, metadata) => {
      console.log('Plaid event:', eventName, metadata);
    };

    const onExit = (error, metadata) => {
      console.log('hits onexit');
      console.log('error: ', error);
      console.log('metadata: ', metadata);
      console.log(metadata.status == 'requires_oauth');
      if (metadata.status == 'requires_oauth') {
        console.log('link token ' + linkToken.value);
      }
      removePlaidLinkToken();
    };

    const removePlaidLinkToken = () => {
      if (localStorage.getItem('plaid_link_token')) {
        localStorage.removeItem('plaid_link_token');
      }
    };

    const editAccount = (account) => {
      store.editAccount(account);
      router.push({
        name: 'EditAccount',
        params: { previousPage: 'ConnectedAccounts' },
      });
    };

    watchEffect(() => {
      accounts.value = store.accountsList;
    });

    onBeforeMount(() => {
      store.getPlaidAccountsList();
    });

    onMounted(async () => {
      const params = new URLSearchParams(window.location.search);
      const oauthState = params.get('oauth_state_id');

      if (oauthState) {
        const token = localStorage.getItem('plaid_link_token');
        if (token) {
          openPlaidLink(token);
        } else {
          console.error('Missing link token on OAuth redirect.');
        }
      }

      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);

      store.getUserAccountData().then(() => {
        loading.value = false;
      });
      loading.value = false;
    });

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const startUpdateFlow = async () => {
      if (!pendingUpdateItemId.value) return;
      try {
        loading.value = true;
        await store.startUpdateMode(pendingUpdateItemId.value);
        await store.getPlaidAccountsList();
        infoMessage.value = '';
      } catch (err) {
        console.error('Error in update flow:', err);
        infoMessage.value =
          'We had trouble refreshing this connection. Please try again.';
      } finally {
        pendingUpdateItemId.value = null;
        loading.value = false;
      }
    };

    // const startUpdateFlow = async () => {
    //   if (!pendingUpdateInstitutionId.value) return;
    //   try {
    //     loading.value = true;
    //     await store.startUpdateMode(pendingUpdateInstitutionId.value);
    //     await store.getPlaidAccountsList();
    //     infoMessage.value = '';
    //   } catch (err) {
    //     console.error('Error in update flow:', err);
    //     infoMessage.value =
    //       'We had trouble refreshing this connection. Please try again.';
    //   } finally {
    //     pendingUpdateInstitutionId.value = null;
    //     loading.value = false;
    //   }
    // };

    if (!store) return null;

    return {
      router,
      openPlaidLink,
      accounts,
      groupedAccounts,
      editAccount,
      parseIconString,
      loading,
      accountConnectionSelection,
      linkAccountAfterSelection,
      premiumUser,
      infoMessage,
      pendingUpdateInstitutionId,
      startUpdateFlow,
      pendingUpdateItemId
    };
  },
};
</script>

<style scoped>
.ca-view {
  animation: viewFadeIn 0.4s ease both;
}

.info-banner {
  background: #eef4ff;
  border: 1px solid #c7d9ff;
  border-left: 4px solid #3b82f6;
  border-radius: var(--radius-md);
  padding: 0.85rem 1rem;
}
.dark-theme .info-banner {
  background: #0d1a30;
  border-color: #1d4ed8;
  color: #e0f2fe;
}
.info-banner-inner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.info-icon {
  color: #3b82f6;
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 1px;
}
.info-text {
  font-size: 0.85rem;
  margin: 0;
  color: #1e3a5f;
  line-height: 1.5;
}
.dark-theme .info-text {
  color: #e0f2fe;
}

.bank-accounts-card {
  background: white;
  border-radius: var(--radius-lg);
  border: 1px solid #f0f0f0;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  animation: cardSlideUp 0.35s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .bank-accounts-card {
  background: #212e3e;
  border-color: #2a3a4e;
}

.account-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.85rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f8f8;
  transition: background var(--transition-fast);
}
.dark-theme .account-row {
  border-bottom-color: #2a3a4e;
}
.account-row:last-child {
  border-bottom: none;
}
.account-row:hover {
  background: #fafafa;
}
.dark-theme .account-row:hover {
  background: #1a2535;
}

.account-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f4f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.dark-theme .account-icon-wrap {
  background: #1a2535;
}

.account-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.account-name {
  font-size: 0.88rem;
  font-weight: 600;
  color: #213547;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-theme .account-name {
  color: white;
}
.account-type {
  font-size: 0.7rem;
  color: #aaa;
}

.account-chevron {
  font-size: 0.72rem;
  color: #ccc;
  flex-shrink: 0;
}
.dark-theme .account-chevron {
  color: #4a5a6e;
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else>
    <MainHeader :title="'Accounts'">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'UserAccount' })"
        />
      </template>
    </MainHeader>
    <div class="view">
      <div class="flex-med-buttons">
        <button
          v-if="!isUserOver18"
          :disabled="!premiumUser"
          class="blue-button mb-1 mb-med-none w-100"
          @click="accountConnectionSelection"
          data-pt-name="BankAccountLinked"
          data-pt-property-provider="Plaid"
        >
          Connect Account
        </button>
        <button
          class="teal-outline-button w-100"
          @click="router.push({ name: 'AddAccount' })"
        >
          Create Account
        </button>
      </div>
      <div v-if="infoMessage" class="info-banner mt-3">
        <p>{{ infoMessage }}</p>

        <button
          v-if="pendingUpdateInstitutionId"
          class="blue-button mt-2 w-100"
          @click="startUpdateFlow"
        >
          Refresh bank connection
        </button>
      </div>
      <div></div>
      <div class="mt-6" v-if="accounts && accounts.length > 0">
        <div
          v-for="(institutionAccounts, institutionName) in groupedAccounts"
          :key="institutionName"
          class="bank-group"
        >
          <h3 class="bank-name">{{ institutionName }}</h3>
          <div
            v-for="account in institutionAccounts"
            :key="account"
            class="settings-list"
            @click="editAccount(account)"
          >
            <div>
              <font-awesome-icon
                :icon="
                  account.icon ? parseIconString(account.icon) : ['fas', 'user']
                "
                :style="{ color: account.iconColor }"
              />
            </div>
            <div>{{ account.displayName }}</div>
          </div>
        </div>
      </div>
      <div class="mt-6 text-center" v-else>
        You have not connected or created any accounts yet.
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from 'vue-router';
import { useMyStore } from '@/store.js';
import { onBeforeMount, onMounted, ref, watchEffect, computed } from 'vue';
import { createPlaidHandler } from '../../utils/plaidHelper';

import MainHeader from '../../components/View-Header.vue';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
export default {
  components: {
    MainHeader,
    SkeletonLoader,
  },
  setup() {
    const router = useRouter();
    const store = useMyStore();
    const linkToken = ref('');
    let accounts = ref([]);
    const loading = ref(true);
    const premiumUser = computed(() => store.user.premiumSubscription);
    const infoMessage = ref('');
    const pendingUpdateInstitutionId = ref(null);

    const groupedAccounts = computed(() => {
      const result = {};
      accounts.value.forEach((account) => {
        if (!result[account.institutionName]) {
          result[account.institutionName] = [];
        }
        result[account.institutionName].push(account);
      });

      const sortedResult = {};
      Object.keys(result)
        .sort()
        .forEach((institutionName) => {
          sortedResult[institutionName] = result[institutionName].sort((a, b) =>
            a.displayName.localeCompare(b.displayName)
          );
        });

      return sortedResult;
    });

    const accountConnectionSelection = () => {
      linkAccountAfterSelection();
    };

    const linkAccountAfterSelection = () => {
      try {
        store.createLinkToken().then(() => {
          linkToken.value = store.linkToken;
          localStorage.setItem('plaid_link_token', linkToken.value);
          openPlaidLink(linkToken.value);
        });
      } catch (error) {
        console.log('Error creating link token:', error);
      }
    };

    const openPlaidLink = (token) => {
      const handler = createPlaidHandler({
        token,
        onSuccess,
        onEvent,
        onExit,
      });
      handler.open();
    };

    // Re-open Link if the Universal Link arrives after handler creation
    window.addEventListener('PlaidOAuthRedirect', (e) => {
      try {
        const token =
          linkToken.value || localStorage.getItem('plaid_link_token');
        if (!token) return;

        const handler = createPlaidHandler({
          token,
          onSuccess,
          onExit,
        });
        handler.open();
      } catch (err) {
        console.error('Resume after OAuth failed:', err);
      }
    });

    const onSuccess = async (publicToken, metadata) => {
      try {
        loading.value = true;
        
        const resp = await store.exchangePublicToken(
          publicToken, 
          metadata.institution, 
          metadata.accounts
        );


        console.log('metadata ' + JSON.stringify(metadata));

        // if (resp?.data?.requiresReauthentication) {
        //   infoMessage.value =
        //     `Looks like ${metadata.institution?.name || 'this bank'} is already connected. ` +
        //     `We just need to refresh the connection, so we’ll reopen your bank login to update it.`;
        //   await store.startUpdateMode(metadata.institution.institution_id);
        //   await store.getPlaidAccountsList();
        //   return;
        // }

        if (resp?.data?.requiresReauthentication) {
          infoMessage.value =
            `Looks like ${metadata.institution?.name || 'this bank'} is already connected. ` +
            `To keep your data in sync, please refresh the connection below.`;
          pendingUpdateInstitutionId.value = metadata.institution.institution_id;
          return;
        }

        if (resp?.data?.success) {
          await store.getPlaidAccountsList();
          await store.syncPlaidTransactions();
        }
        
      } catch (error) {
        console.error('Error handling Plaid success:', error);
        
        if (error.message.includes('duplicate') || error.response?.data?.requiresReauthentication) {
          // await store.startUpdateMode(metadata.institution.institution_id);
          // await store.getPlaidAccountsList();
          // return;
           infoMessage.value =
              `This bank is already linked. Please refresh the connection below so we can keep your data up to date.`;
            pendingUpdateInstitutionId.value = metadata.institution.institution_id;
            return;
        }
      } finally {
        loading.value = false;
        removePlaidLinkToken();
      }
    };

    // const onSuccess = async (publicToken, metadata) => {
    //   try {
    //     loading.value = true;
    //     // await store.exchangePublicToken(
    //     //   publicToken,
    //     //   metadata.institution,
    //     //   metadata.accounts
    //     // );
    //     // await store.getPlaidAccountsList();
    //     // await store.syncPlaidTransactions();

    //     const resp = await store.exchangePublicToken(publicToken, metadata.institution, metadata.accounts);

    //     if (resp?.data?.startedUpdateMode && resp?.data?.linkToken) {
    //       const token = resp.data.linkToken;
    //       linkToken.value = token;
    //       localStorage.setItem('plaid_link_token', token);
    //       openPlaidLink(token);
    //       return;
    //     }

    //   } catch (error) {
    //     console.error('Error handling Plaid success:', error);
    //   } finally {
    //     loading.value = false;
    //     removePlaidLinkToken();
    //   }
    // };

    // const onEvent = (eventName, metadata) => {
    //   console.log('Plaid event:', eventName, metadata);

    //   if (eventName === 'HANDOFF' && metadata.status === 'requires_oauth') {
    //     window.location.href = 'https://luckyfridayapp.com/plaid-oauth-redirect.html' +
    //       '?link_token=' + encodeURIComponent(linkToken.value);
    //     return;
    //   }
    // };

    const onEvent = (eventName, metadata) => {
      console.log('Plaid event:', eventName, metadata);

      // if (eventName === 'OPEN_OAUTH') {
      //   const token = linkToken.value;
      //   const redirect = 'https://luckyfridayapp.com/plaid-oauth-redirect.html';

      //   window.location.href = `${redirect}?link_token=${encodeURIComponent(token)}`;
      // }
    };

    const onExit = (error, metadata) => {
      console.log('hits onexit');
      console.log('error: ', error);
      console.log('metadata: ', metadata);
      console.log(metadata.status == 'requires_oauth');
      if (metadata.status == 'requires_oauth') {
        console.log('link token ' + linkToken.value);
      }
      removePlaidLinkToken();
      // Handle the situation when your user exits Link
    };

    const removePlaidLinkToken = () => {
      if (localStorage.getItem('plaid_link_token')) {
        localStorage.removeItem('plaid_link_token');
      }
    };

    const editAccount = (account) => {
      store.editAccount(account);
      router.push({
        name: 'EditAccount',
        params: { previousPage: 'ConnectedAccounts' },
      });
    };

    watchEffect(() => {
      accounts.value = store.accountsList;
    });

    onBeforeMount(() => {
      store.getPlaidAccountsList();
    });

    onMounted(async () => {
      const params = new URLSearchParams(window.location.search);
      const oauthState = params.get('oauth_state_id');

      if (oauthState) {
        const token = localStorage.getItem('plaid_link_token');
        if (token) {
          openPlaidLink(token);
        } else {
          console.error('Missing link token on OAuth redirect.');
        }
      }

      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);
      store.getUserAccountData().then(() => {
        loading.value = false;
      });

      loading.value = false;
    });

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const startUpdateFlow = async () => {
      if (!pendingUpdateInstitutionId.value) return;

      try {
        loading.value = true;
        await store.startUpdateMode(pendingUpdateInstitutionId.value);
        await store.getPlaidAccountsList();
        infoMessage.value = '';
      } catch (err) {
        console.error('Error in update flow:', err);
        infoMessage.value =
          'We had trouble refreshing this connection. Please try again.';
      } finally {
        pendingUpdateInstitutionId.value = null;
        loading.value = false;
      }
    };

    if (!store) {
      return null;
    }

    return {
      router,
      openPlaidLink,
      accounts,
      groupedAccounts,
      editAccount,
      parseIconString,
      loading,
      accountConnectionSelection,
      linkAccountAfterSelection,
      premiumUser,
      infoMessage,
      pendingUpdateInstitutionId,
      startUpdateFlow
    };
  },
};
</script>
<style scoped>
.settings-list {
  display: grid;
  grid-template-columns: 10% 90%;
  box-shadow: 0px 3px 20px rgba(0, 0, 0, 0.1);
  padding: 1em;
  border-radius: 5px;
  cursor: pointer;
  margin-bottom: 1em;
}

.light-theme .settings-list {
  background: white;
}

.dark-theme .settings-list {
  background: #212e3e;
  color: white;
  border: 1px solid white;
}

.bank-name {
  text-align: center;
  padding-bottom: 0.5em;
  font-size: 1.1em !important;
  font-weight: bold;
}

.light-theme .bank-name {
  color: #004751;
}

.dark-theme .bank-name {
  color: white;
}

.bank-group {
  padding: 1em;
  border-radius: 5px;
  margin-bottom: 2.5em;
}

.light-theme .bank-group {
  background: #f8f8f8;
  box-shadow: 0px 20px 20px rgba(0, 0, 0, 0.1);
}

.dark-theme .bank-group {
  background: #212e3e;
  color: white;
}

.info-banner {
  padding: 0.75em 1em;
  border-radius: 5px;
  font-size: 0.9rem;
  text-align: left;
}

.light-theme .info-banner {
  background: #e6f4ff;
  color: #004751;
  border: 1px solid #b3dcff;
}

.dark-theme .info-banner {
  background: #102a43;
  color: #e0f2fe;
  border: 1px solid #1d4ed8;
}
</style> -->
